import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useDeviceType from "../../../custom-hooks/DeviceType";
import EventEHRSwitchButton from "../../../component-lib/Molecules/switch-button/EventEHRSwitchButton";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Heading from "../../../component-lib/Atoms/heading/EventEHRHeading";
import { plusIcon, rightDirectionBlackIcon, searchIcon } from "../../../assets";
import Button from "../../../component-lib/Molecules/button/Button";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import SearchSelectDropdown from "../../../component-lib/Atoms/SearchSelectDropdown/EventEHRSearchSelectDropdown";
import FormattedTooltip from "../../../component-lib/Molecules/tooltip/FormattedTooltip";
import TemplateDialog from "../dialogs/TemplateDialog";
import TemplateImportResultsDialog from "../dialogs/TemplateImportResultsDialog";
import {
  getTemplateList,
  getTemplateNameOptions,
  patchTemplateData,
  postTemplateData,
} from "../SettingsSaga";
import {
  componentKey,
  setCloseAddTemplateDialog,
  setOpenTemplateImportResultsDialog,
  setTemplateImportResults,
} from "../SettingsSlice";

const TemplateScreen = () => {
  const {
    templateList,
    templateNameOptions,
    closeAddTemplateDialog,
    templateImportResults,
    openTemplateImportResultsDialog,
  } = useSelector((state: any) => state[componentKey]);

  const location = useLocation();
  const { template } = location.state || {};

  const { selectedTemplate } = useParams();

  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [rowPerPage, setRowPerPage] = useState("10");
  const [pageNumber, setPageNumber] = useState(1);
  const [searchedValue, setSearchValue] = useState<string>("");
  const [status, setStatus] = useState("");
  const [defaultTemplateDataRow, setDefaultTemplateDataRow] = useState<any>({});

  // OPEN ADD/EDIT TEMPLATE DIALOG
  const [dialogState, setDialogState] = useState<null | "add" | "edit">(null);

  const deviceType = useDeviceType();

  // EMAIL TEMPLATE COLUMN DATA
  const TemplateColumns = [
    {
      field: "templateName",
      label: <div>{selectedTemplate === "notes-template" ? "Instruction Title" : "Template Name"}</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">{row?.templateName}</p>
        );
      },
    },
    {
      field: "description",
      label: <div>Description</div>,
      renderLogic: (row: any) => {
        return (
          <FormattedTooltip label={row?.description || "-"} maxWidth={500} />
        );
      },
    },
    {
      field: "status",
      label: <div>Status</div>,
      renderLogic: (row: any, index: any) => {
        console.log("status:", row);

        const status =
          row?.status === "Active"
            ? true
            : row?.status === "InActive"
              ? false
              : false;
        return (
          <div className="text-neutral-90 font-normal flex justify-start gap-1 items-center">
            <EventEHRSwitchButton
              checked={status}
              onChange={() => {
                const apiPayload = {
                  payload: {
                    status: status ? "InActive" : "Active",
                  },
                  templateId: row?.id,
                };
                dispatch(patchTemplateData(apiPayload));
              }}
            />
          </div>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: template === "Discharge Instructions" ? "Edit Instruction" : "Edit Template",
                    onClickCb: () => {
                      setDefaultTemplateDataRow(row);
                      setDialogState("edit"); // Open edit dialog
                    },
                  },
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  // HANDLE SEARCH CHANGE VALUE AND ADDED SOME DELAY
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPageNumber(1);

    const value = e.target.value;

    // CLEAR THE PREVIOUS TIMEOUT
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // SET A NEW TIMEOUT TO DELAY THE CALLBACK
    typingTimeoutRef.current = setTimeout(() => {
      setSearchValue(value?.trim());
    }, 500); // ADJUST DELAY AS NEEDED
  };

  // TEMPLATE LIST ROW DATA

  const templateRowData = templateList?.templates
    // ?.filter((data: any) =>
    //   selectedTemplate === "sms-template"
    //     ? data.type === "Message"
    //     : data.type === "Email"
    // )
    ?.map((data: any) => ({
      id: data?.id || "-",
      templateName: data?.name || "-",
      description: data?.description || "-",
      status: data?.status || "",
    }));

  // CURRENT TEMPLATE TYPE (Message / Note / Email)
  const templateType =
    selectedTemplate === "sms-template"
      ? "Message"
      : selectedTemplate === "notes-template"
        ? "Note"
        : "Email";

  // FETCH ALL NAMES OF THIS TYPE TO POPULATE THE "TEMPLATE NAME" DROPDOWN
  // (custom templates). Not needed for notes-template which uses a free-text title.
  const getTemplateNameOptionsList = () => {
    if (selectedTemplate === "notes-template") return;
    dispatch(getTemplateNameOptions({ type: templateType }));
  };

  useEffect(() => {
    getTemplate();
  }, [searchedValue, rowPerPage, pageNumber, status]);

  useEffect(() => {
    getTemplateNameOptionsList();
  }, [selectedTemplate]);

  useEffect(() => {
    if (closeAddTemplateDialog) {
      dispatch(setCloseAddTemplateDialog(false));
      setDialogState(null);
      getTemplate();
      getTemplateNameOptionsList();
    }
  }, [closeAddTemplateDialog]);

  useEffect(() => {
    if (openTemplateImportResultsDialog) {
      getTemplate();
    }
  }, [openTemplateImportResultsDialog]);

  const getTemplate = () => {
    const payload = {
      pageNumber: pageNumber,
      limit: rowPerPage,
      searchValue: searchedValue?.trim(),
      status: status ? status : "",
      type:
        selectedTemplate === "sms-template"
          ? "Message"
          : selectedTemplate === "notes-template"
            ? "Note"
            : "Email",
    };
    dispatch(getTemplateList(payload));
  };

  const addTemplate = (data: any) => {
    console.log("data:", data);

    try {
      const payload = {
        type:
          selectedTemplate === "sms-template"
            ? "Message"
            : selectedTemplate === "notes-template"
              ? "Note"
              : "Email",
        name: data?.templateName,
        description: data?.description,
        status: "Active",
      };
      if (defaultTemplateDataRow?.templateName) {
        const templateUpdatePayload = {
          payload: {
            ...payload,
          },
          templateId: defaultTemplateDataRow?.id,
        };
        dispatch(patchTemplateData(templateUpdatePayload));
      } else {
        dispatch(postTemplateData(payload));
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <WrapperCard
      customClasses="m-3 p-3 flex-1 min-h-0"
      customStyle={{ minWidth: deviceType !== "mobile" ? "400px" : "" }}
    >
      <div className="flex flex-col h-full gap-3">
        <div
          className={`flex ${deviceType === "mobile" ? "flex-col items-start gap-3" : "items-center"} justify-between w-full`}
        >
          <div className="flex items-center gap-3">
            <img
              className="cursor-pointer"
              src={rightDirectionBlackIcon}
              alt="back-arrow"
              style={{ height: "12.5px" }}
              onClick={() => {
                navigate("/settings");
              }}
            />
            <Heading
              title={template === "Discharge Instructions" ? "Discharge Instructions" : `${template} Template`}
              customStyle={{ color: "#1D1D1D" }}
            />
          </div>

          <div
            className={`flex ${deviceType === "mobile" ? "w-full justify-between" : ""} gap-3`}
          >
            <div style={{ maxWidth: "350px" }}>
              <SearchSelectDropdown
                freeSolo={true}
                searchedValue={searchedValue}
                onSearchValueChangeCb={(e) => handleChange(e)}
                size="small"
                label={template === "Discharge Instructions" ? "Search by Instruction Title" : `Search by ${template} Template Name`}
                fullWidth={deviceType === "mobile" ? "160px" : "320px"}
                startIcon={
                  <img
                    alt="search-icon"
                    src={searchIcon}
                    width={"18px"}
                    height={"18px"}
                  />
                }
              />
            </div>
            <Button
              startIcon={
                <img
                  src={plusIcon}
                  alt={"search"}
                  className="cursor-pointer w-[19px] h-[19px]"
                />
              }
              label={template === "Discharge Instructions" ? "Add Discharge Instructions" : `Add ${template} Template`}
              variant="contained"
              onClickCb={() => {
                setDefaultTemplateDataRow({});
                setDialogState("add"); // Open add dialog
              }}
              style={{
                paddingTop: "6px",
                paddingBottom: "6px",
                minWidth: "155px",
              }}
            ></Button>
          </div>
        </div>
        <div style={{ overflow: "auto" }}>
          <Table
            columns={TemplateColumns}
            rows={templateRowData}
            paginationProps={{
              isPagination: true,
              page: pageNumber,
              totalPages: templateList?.totalPages,
              totalCount: templateList?.totalRecords,
              limit: rowPerPage,
              rowsPerPageOptions: [10, 25, 50],
              onPageChange: (page: any) => {
                setPageNumber(page);
              },
              handleChangeRowsPerPage: (rowPerPage: any) => {
                setRowPerPage(rowPerPage);
                setPageNumber(1);
              },
            }}
          />
        </div>
      </div>

      {/* ADD TEMPLATE DIALOG */}

      {dialogState && (
        <TemplateDialog
          defaultTemplateData={defaultTemplateDataRow}
          template={template}
          existingTemplateNames={(templateNameOptions || [])
            .map((t: { name?: string }) => t?.name)
            .filter(Boolean)}
          templateType={templateType}
          isEdit={dialogState === "edit"} // Determine edit mode
          isOpen={!!dialogState} // Dialog is open if dialogState is not null
          handleClose={() => {
            setDialogState(null); // Close the dialog by setting to null
          }}
          handleData={(data) => {
            addTemplate(data);
          }}
        />
      )}

      {/* Template Import Results Dialog */}
      <TemplateImportResultsDialog
        isOpen={openTemplateImportResultsDialog}
        handleClose={() => {
          dispatch(setOpenTemplateImportResultsDialog(false));
          dispatch(setTemplateImportResults(null));
        }}
        results={templateImportResults}
      />
    </WrapperCard>
  );
};

export default TemplateScreen;
